import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Widget } from "@/components/dashboard/Widgets";
import { Badge } from "@/components/ui/badge";

export default function SharedDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const filters = { region: "all", team: "all", range: "30d" };

  useEffect(() => {
    const load = () => api.get(`/shared/dashboard/${token}`, { params: { range: "30d" } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invalid link"));
    load();
    const id = setInterval(() => { load(); setTick((t) => t + 1); }, 6000);
    return () => clearInterval(id);
  }, [token]);

  const summaryMap = useMemo(() => {
    const m = {};
    (data?.summary || []).forEach((s) => { m[s.key] = s; });
    return m;
  }, [data]);

  const catalog = useMemo(() => {
    const c = {};
    (data?.summary || []).forEach((s) => { c[s.key] = { key: s.key, label: s.label }; });
    return c;
  }, [data]);

  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <p className="text-destructive font-mono" data-testid="shared-dash-error">{error}</p>
        <Link to="/" className="text-primary hover:underline text-sm">Go home</Link>
      </div>
    );

  if (!data)
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground animate-pulse font-mono text-sm">Loading shared dashboard…</div>;

  const dataBase = `/shared/dashboard/${token}`;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Activity className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold tracking-tight">PULSE / KPI</span>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">Shared · read-only</Badge>
          </div>
          <Link to="/login" className="text-sm text-primary hover:underline" data-testid="shared-dash-signin">Sign in</Link>
        </div>
      </header>
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Shared Dashboard</p>
        <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="shared-dash-title">{data.name}</h1>
      </div>
      <main className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[minmax(140px,auto)]" data-testid="shared-dash-grid">
          {(data.widgets || []).map((w) => {
            const span = w.type === "kpi" ? "md:col-span-1" : w.span === 2 ? "md:col-span-2" : w.span === 3 ? "md:col-span-3" : "md:col-span-1";
            return (
              <div key={w.id} className={`col-span-1 ${span}`}>
                <Widget widget={w} summaryMap={summaryMap} filters={filters} tick={tick} catalog={catalog} dataBase={dataBase} />
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4 font-mono">Read-only live snapshot · updates automatically.</p>
      </main>
    </div>
  );
}
